/**
 * Whiteboard State Management
 * static/js/whiteboard/state.js
 * 
 * 狀態管理
 */

// 當前狀態
let state = {
  currentDrawingId: null,
  currentTitle: '未命名白板',
  isDirty: false,
  lastSavedHash: null,
  isLoading: false,
  excalidrawAPI: null,
  whiteboards: [],
};

// 狀態變更監聽器
const listeners = new Set();

/**
 * 訂閱狀態變更
 * @param {Function} listener 
 * @returns {Function} 取消訂閱函數
 */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * 通知所有監聽器
 */
function notify() {
  listeners.forEach(listener => listener(state));
}

/**
 * 取得當前狀態
 * @returns {Object}
 */
export function getState() {
  return { ...state };
}

/**
 * 設定 Excalidraw API 參考
 * @param {Object} api 
 */
export function setExcalidrawAPI(api) {
  state.excalidrawAPI = api;
  notify();
}

/**
 * 取得 Excalidraw API
 * @returns {Object|null}
 */
export function getExcalidrawAPI() {
  return state.excalidrawAPI;
}

/**
 * 設定當前白板 ID
 * @param {number|null} id 
 */
export function setCurrentDrawingId(id) {
  state.currentDrawingId = id;
  notify();
}

/**
 * 取得當前白板 ID
 * @returns {number|null}
 */
export function getCurrentDrawingId() {
  return state.currentDrawingId;
}

/**
 * 設定當前標題
 * @param {string} title 
 */
export function setCurrentTitle(title) {
  state.currentTitle = title;
  notify();
}

/**
 * 取得當前標題
 * @returns {string}
 */
export function getCurrentTitle() {
  return state.currentTitle;
}

/**
 * 設定是否有未儲存變更
 * @param {boolean} dirty 
 */
export function setDirty(dirty) {
  state.isDirty = dirty;
  notify();
}

/**
 * 檢查是否有未儲存變更
 * @returns {boolean}
 */
export function isDirty() {
  return state.isDirty;
}

/**
 * 設定上次儲存的 hash
 * @param {string|null} hash 
 */
export function setLastSavedHash(hash) {
  state.lastSavedHash = hash;
}

/**
 * 取得上次儲存的 hash
 * @returns {string|null}
 */
export function getLastSavedHash() {
  return state.lastSavedHash;
}

/**
 * 設定載入狀態
 * @param {boolean} loading 
 */
export function setLoading(loading) {
  state.isLoading = loading;
  notify();
}

/**
 * 檢查是否正在載入
 * @returns {boolean}
 */
export function isLoading() {
  return state.isLoading;
}

/**
 * 設定白板列表
 * @param {Array} whiteboards 
 */
export function setWhiteboards(whiteboards) {
  state.whiteboards = whiteboards;
  notify();
}

/**
 * 取得白板列表
 * @returns {Array}
 */
export function getWhiteboards() {
  return state.whiteboards;
}

/**
 * 新增白板到列表
 * @param {Object} whiteboard 
 */
export function addWhiteboard(whiteboard) {
  state.whiteboards = [whiteboard, ...state.whiteboards];
  notify();
}

/**
 * 從列表移除白板
 * @param {number} id 
 */
export function removeWhiteboard(id) {
  state.whiteboards = state.whiteboards.filter(w => w.id !== id);
  notify();
}

/**
 * 更新列表中的白板
 * @param {number} id 
 * @param {Object} updates 
 */
export function updateWhiteboardInList(id, updates) {
  state.whiteboards = state.whiteboards.map(w => 
    w.id === id ? { ...w, ...updates } : w
  );
  notify();
}

/**
 * 重置狀態（新建白板時使用）
 */
export function resetState() {
  state = {
    currentDrawingId: null,
    currentTitle: '未命名白板',
    isDirty: false,
    lastSavedHash: null,
    isLoading: false,
    excalidrawAPI: state.excalidrawAPI, // 保留 API 參考
    whiteboards: state.whiteboards, // 保留列表
  };
  notify();
}

/**
 * 計算元素的 hash（用於比較是否有變更）
 * @param {Array} elements 
 * @returns {string}
 */
export function hashElements(elements) {
  if (!elements || elements.length === 0) {
    return 'empty';
  }
  // 包含位置、大小、角度等重要屬性來偵測變更
  const summary = elements.map(el => {
    // 使用多個屬性確保能偵測到所有類型的變更
    const props = [
      el.id,
      el.version || 0,
      el.versionNonce || 0,
      Math.round(el.x || 0),
      Math.round(el.y || 0),
      Math.round(el.width || 0),
      Math.round(el.height || 0),
      Math.round((el.angle || 0) * 1000),
      el.isDeleted ? 1 : 0,
    ];
    return props.join(':');
  }).join(',');
  
  // 使用簡單的 hash 函數
  let hash = 0;
  for (let i = 0; i < summary.length; i++) {
    const char = summary.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(36);
}

/**
 * 檢查是否有變更（比較當前狀態與上次儲存的 hash）
 * @returns {boolean}
 */
export function hasChanges() {
  const api = state.excalidrawAPI;
  if (!api) return false;
  
  const elements = api.getSceneElements();
  const currentHash = hashElements(elements);
  
  return currentHash !== state.lastSavedHash;
}
